import {ReactComponent as CloseIcon} from "components/Icons/close.svg";
import React, {useEffect, useState} from 'react';
import {Spinner} from 'components/UIComponents/Spinner';
import {ReactComponent as StuffIcon} from "components/Icons/chevron-down.svg";
import {ReactComponent as DownloadIcon} from "components/Icons/download.svg";
import {ReactComponent as CopyIcon} from "components/Icons/copy.svg";
import {Document, Page, pdfjs} from 'react-pdf';
import {SecondaryButton} from "components/UIComponents/SecondaryButton";
import {FormattedMessage} from "react-intl";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import { menuEditorActions } from "features/editor/editorSlice";
import { IconButton } from "components/UIComponents/IconButton";
import {ReactComponent as PrinterIcon} from 'components/Icons/printer.svg';
import copy from "copy-to-clipboard";
import {authSelectors} from "../auth/authSlice";
import {fetchCustomerAddressData} from "../../lib/api/wineAppApi";
import {useParams} from "react-router";
import {menuManagementSelectors} from "../menu/menumanagement/menuManagementSlice";
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.js"

export interface PDFPreviewProps {
    open: boolean
    pdf: any
    error: boolean
    onClose: () => void
}

export default function PDFPreview(props: PDFPreviewProps) {
    const dispatch = useAppDispatch();

    const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);
    const [restaurantName, setRestaurantName] = useState<string>("");
    const soldTo = useAppSelector(authSelectors.customer);

    const [height, setHeight] = useState<number | null>(null);
    const [pageNr, setPageNr] = useState<number | null>(null);
    const [maxPageNr, setMaxPageNr] = useState<number | null>(null);

    const [isCopyTooltipShown, setCopyTooltipShow] = useState<boolean>(true);

    const menuId = useParams()['menuId'];
    const userId = useAppSelector(authSelectors.customer);
    const menuName = useAppSelector(menuManagementSelectors.menuName(userId, menuId));

    const download = async () => {
        if (downloadInProgress) return;
        setDownloadInProgress(true);
        const file = await fetch(props.pdf);
        setDownloadInProgress(false);
        const blob = await file.blob();
        const el = window.document.createElement('a');
        el.href = window.URL.createObjectURL(blob);
        el.download = ((restaurantName||null)??userId) + "_" + menuName + "_" + new Date().toISOString().substring(0,10) + ".pdf";
        document.body.appendChild(el);
        el.click();
        document.body.removeChild(el);
    };

    const handleLoadSucccess = ({ numPages }: any) => {
        setPageNr(1);
        setMaxPageNr(numPages);
    };

    useEffect(()=>{
        if (restaurantName || soldTo === null) return;
        setDownloadInProgress(true);
        fetchCustomerAddressData(soldTo)
            .then(addressData => {
                setRestaurantName(addressData.name);
                setDownloadInProgress(false);
            })
            .catch(e => {
                setDownloadInProgress(false);
            });
    }, [soldTo, restaurantName]);

    const handleLastPage = () => {
        if (pageNr && pageNr > 1) {
            setPageNr(pageNr - 1);
        }
    };

    const handleNextPage = () => {
        if (pageNr && maxPageNr && pageNr < maxPageNr) {
            setPageNr(pageNr + 1)
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const openPrintPopover = () => dispatch(menuEditorActions.openPrintPopover());

    const copyLinkToClipboard = () => {
        setCopyTooltipShow(true);

        copy(`${((restaurantName||null)??userId)} ${menuName}: ${props.pdf}`);
    };

    useEffect(()=>{
        if (!isCopyTooltipShown) return;
        const to = setTimeout(()=>setCopyTooltipShow(false), 3000);
        return () => {
            clearTimeout(to);
        }
    }, [isCopyTooltipShown]);

    return (
        <>{props.open &&
          <div className="w-full h-full flex flex-col items-stretch bg-gray-1">
            <div className="fixed top-0 right-0 p-10 flex gap-3 z-popover">
                {props.pdf && <>
                    <div className="relative">
                        <IconButton onClick={copyLinkToClipboard}>
                            <CopyIcon className="h-5 w-5 text-text" />
                        </IconButton>
                        {isCopyTooltipShown&&<div className="absolute left-1/2
                            -translate-x-1/2 whitespace-nowrap
                            rounded-md drop-shadow-dropdown-menu bg-white
                            font-barlow text-base p-3 z-popover">
                            <FormattedMessage id="pdfpreview.linkCopied" />
                        </div>}
                    </div>

                    <div title="Download" className="bg-white rounded-full border border-secondary
                            px-2 py-2 text-secondary w-11 h-11 flex justify-center items-center cursor-pointer"
                         onClick={download}
                    >
                        {!downloadInProgress&&<DownloadIcon className="h-5 w-5 text-text" />}
                        {downloadInProgress&&<Spinner className="h-5 w-5"/>}
                    </div>

                    <IconButton onClick={() => openPrintPopover()}>
                        <PrinterIcon className="h-5 w-5 text-text" />
                    </IconButton>
                </>}

                <IconButton onClick={props.onClose}>
                    <CloseIcon className="h-5 w-5 text-primary"/>
                </IconButton>
            </div>
            {props.pdf && !props.error ?
                <>
                    <Document
                        className="flex justify-center items-center w-full h-full overflow-scroll"
                        file={props.pdf}
                        onLoadSuccess={handleLoadSucccess}
                        onLoadError={console.error}
                    >
                        {pageNr != null && <Page height={height || window.innerHeight} pageNumber={pageNr as number} />}
                    </Document>
                    <div className="fixed left-0 right-0 bottom-3 flex justify-center">
                        <div className="bg-white p-3 flex gap-3" style={{boxShadow: '0 .5rem 1rem rgba(0,0,0,.3)'}}>
                            <SecondaryButton
                                onClick={() => handleLastPage()}
                                style={{opacity: pageNr === 1 ? 0.5 : 1}}
                            >
                                <StuffIcon style={{transform: 'rotate(90deg)'}}/>
                            </SecondaryButton>

                            <span>
                                <FormattedMessage id="pdfpreview.page" /> {pageNr} <FormattedMessage id="pdfpreview.of" /> {maxPageNr}
                            </span>

                            <SecondaryButton
                                onClick={() => handleNextPage()}
                                style={{opacity: pageNr === maxPageNr ? 0.5 : 1}}
                            >
                                <StuffIcon style={{transform: 'rotate(-90deg)'}}/>
                            </SecondaryButton>
                        </div>
                    </div>
                </>
                :
                    props.error ?
                        <div className="w-screen h-screen flex justify-center items-center">
                            <FormattedMessage id="pdfpreview.error" />
                        </div>
                    :
                        <div className="w-screen h-screen flex justify-center items-center">
                            <Spinner className="w-10 h-10 m-auto"/>
                        </div>
            }
          </div>
        }</>
    );
}
